import cloneDeep from 'lodash/cloneDeep';
import floor from 'lodash/floor';
import React, { useRef } from 'react';
import { getOperateType, IFramework, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { flipPoint, getLineIntegerPoints, rotatePoint, translateYReverse } from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IPoint, LayerType } from '@/version-types';
import { isImageOutOfRangeLayer } from '../utils';

const MoveImageAction: React.FC<{
    stageScale: number;
    framework: IFramework;
}> = ({ stageScale, framework }) => {
    const currentLayerRef = useRef<ILayerProps | undefined>(undefined);
    const lastPointRef = useRef<IPoint>({ x: 0, y: 0 });

    useMoveEventListener((e: ITouchKitEvent) => {
        if (e.isStart) {
            const layer = framework.operate.getActivePickLayer();
            currentLayerRef.current = cloneDeep(layer as ILayerProps);
            lastPointRef.current = { x: e.startPoint.x, y: e.startPoint.y };
        }
        // 渲染层不存在，不可操作
        if (!currentLayerRef.current || currentLayerRef.current?.layerType !== LayerType.IMAGE_LAYER) {
            return null;
        }

        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');
        if (!getOperateType(target)) return;

        if (dataType !== 'crop-move') return;

        const { width, height, data, rotate } = currentLayerRef.current;
        const { imageTransform } = data as IImageLayerProps;
        const { offsetX, offsetY, widthRatio, heightRatio, scale, flipH, flipV, rotate: imageRotate } = imageTransform;

        const points = getLineIntegerPoints(
            lastPointRef.current.x,
            lastPointRef.current.y,
            lastPointRef.current.x + e.deltaX,
            lastPointRef.current.y + e.deltaY,
        );

        for (let i = points.length - 1; i >= 0; i--) {
            const displacement = {
                x: 0,
                y: 0,
            };
            const newLayer = cloneDeep(currentLayerRef.current);
            const point = points[i];
            displacement.x = point.x - lastPointRef.current.x;
            displacement.y = point.y - lastPointRef.current.y;

            // 将移动的位置进行坐标反向旋转  旋转rotate是图层的旋转角度
            const currentMoveDistance = translateYReverse(
                flipPoint(
                    rotatePoint(
                        translateYReverse({
                            x: displacement.x / stageScale,
                            y: displacement.y / stageScale,
                        }),
                        rotate,
                    ),
                    flipH,
                    flipV,
                ),
            ) as IPoint;

            (newLayer.data as IImageLayerProps).imageTransform.offsetX =
                offsetX + currentMoveDistance.x / (widthRatio * width * scale);
            (newLayer.data as IImageLayerProps).imageTransform.offsetY =
                offsetY + currentMoveDistance.y / (heightRatio * height * scale);
            const isOut = isImageOutOfRangeLayer(newLayer);
            if (!isOut) {
                Object.assign(imageTransform, (newLayer.data as IImageLayerProps).imageTransform);
                lastPointRef.current = { x: point.x, y: point.y };
                break;
            }

            // 如果超出范围 那就只移动x或只移动y看看是否超出 没超出照样移动就好了
            if (isOut) {
                // 移动的距离转化为图片坐标系
                const imageOffsetPosition = flipPoint(
                    rotatePoint(translateYReverse(currentMoveDistance), imageRotate),
                    flipH,
                    flipV,
                );

                // 只取一边的位移  然后再转为图层的坐标系
                const onlyOffsetX = translateYReverse(
                    flipPoint(
                        rotatePoint(
                            {
                                x: imageOffsetPosition.x,
                                y: 0,
                            },
                            -imageRotate,
                        ),
                        flipH,
                        flipV,
                    ),
                );

                (newLayer.data as IImageLayerProps).imageTransform.offsetX =
                    offsetX + onlyOffsetX.x / (widthRatio * width * scale);
                (newLayer.data as IImageLayerProps).imageTransform.offsetY =
                    offsetY + onlyOffsetX.y / (heightRatio * height * scale);

                if (!isImageOutOfRangeLayer(newLayer)) {
                    Object.assign(imageTransform, (newLayer.data as IImageLayerProps).imageTransform);
                    lastPointRef.current = { x: point.x, y: point.y };
                    break;
                }

                const onlyOffsetY = translateYReverse(
                    flipPoint(
                        rotatePoint(
                            {
                                x: 0,
                                y: imageOffsetPosition.y,
                            },
                            -imageRotate,
                        ),
                        flipH,
                        flipV,
                    ),
                );
                (newLayer.data as IImageLayerProps).imageTransform.offsetX =
                    offsetX + onlyOffsetY.x / (widthRatio * width * scale);
                (newLayer.data as IImageLayerProps).imageTransform.offsetY =
                    offsetY + onlyOffsetY.y / (heightRatio * height * scale);

                if (!isImageOutOfRangeLayer(newLayer)) {
                    Object.assign(imageTransform, (newLayer.data as IImageLayerProps).imageTransform);
                    lastPointRef.current = { x: point.x, y: point.y };
                    break;
                }
            }
        }

        imageTransform.offsetX = floor(imageTransform.offsetX, 8);
        imageTransform.offsetY = floor(imageTransform.offsetY, 8);
        const resultLayer = cloneDeep(currentLayerRef.current);
        currentLayerRef.current = cloneDeep(resultLayer);
        framework.stage.addOrUpdate(cloneDeep(resultLayer));
    });
    return null;
};

export default React.memo(MoveImageAction);
